import React, { useCallback } from 'react';

import { QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Col, Row, Spin, Tooltip } from 'antd';

import { Field, useFormikContext } from 'formik';
import { SelectFormField } from 'v2/components/Shared/FormFields/SelectFormField';
import { SelectProduct } from 'v2/components/Shared/FormFields/SelectProduct';
import { TextFormField } from 'v2/components/Shared/FormFields/TextFormField';
import UrlValidationFormField from 'v2/components/Shared/UrlValidationFormField';
import { WebhookContentTypeAlias, WebhookTypes } from 'v2/interfaces/webhook2';
import type { FieldsWebHook } from 'v2/interfaces/webhook2';

import { useWebhookContext } from '../context';

const MOZ_CONTENT_TYPE_URL = 'https://developer.mozilla.org/pt-BR/docs/Web/HTTP/Headers/Content-Type';

enum ProductSelectType {
  all = 'all',
  custom = 'custom'
}

export const ALL_PRODUCTS_ID = -1;

const Fields = React.memo(() => {
  const { setSelectedWebhook, setIsFormOpen } = useWebhookContext();
  const { isSubmitting, isValid, values, setValues } = useFormikContext<FieldsWebHook>();
  const [isUrlValidated, setIsUrlValidated] = React.useState(false);
  const [resetValidation, setResetValidation] = React.useState(0);

  let defaultProductSelectType = null;

  if (values.product_id.length) {
    defaultProductSelectType = values.product_id.includes(ALL_PRODUCTS_ID)
      ? ProductSelectType.all
      : ProductSelectType.custom;
  }

  const [productSelectType, setProductSelectType] = React.useState<ProductSelectType>(defaultProductSelectType);

  const setCustomProductSelectType = () => {
    setProductSelectType(ProductSelectType.custom);
    setValues({ ...values, product_id: customSelectedProducts });
  };

  const setAllProductSelectType = () => {
    setProductSelectType(ProductSelectType.all);

    if (!customSelectedProducts.includes(ALL_PRODUCTS_ID)) {
      setCustomSelectedProducts(values.product_id);
      setValues({ ...values, product_id: [ALL_PRODUCTS_ID] });
    }
  };

  const [customSelectedProducts, setCustomSelectedProducts] = React.useState<number[]>([]);

  const onClose = React.useCallback(() => {
    setSelectedWebhook(null);
    setIsFormOpen(false);
  }, [setIsFormOpen, setSelectedWebhook]);

  const editing = !!values?._id;

  const unvalidateOnChange = useCallback(() => {
    setResetValidation(prev => prev + 1);
    setIsUrlValidated(false);
  }, [setIsUrlValidated]);

  return (
    <>
      <div className='mt-6!'>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Field
              id='description'
              name='description'
              label='Nome da configuração *'
              fullWidth
              component={TextFormField}
            />
          </Col>

          <Col xs={24} md={12}>
            <Field
              id='type'
              name='type'
              label='Tipo (evento) *'
              disabled={editing}
              fullWidth
              options={WebhookTypes}
              component={SelectFormField}
            />
          </Col>

          <Col span={24}>
            <Row gutter={24}>
              <Col span={24}>Os eventos de quais produtos você deseja receber?</Col>

              <Col xs={12}>
                <Tooltip title='Caso algum produto seja cadastrado futuramente em sua conta, você receberá os webhooks relacionados aos novos produtos também'>
                  <Button
                    size='large'
                    style={{ paddingTop: 30, paddingBottom: 30, width: '100%' }}
                    type={productSelectType === ProductSelectType.all ? 'primary' : 'default'}
                    onClick={setAllProductSelectType}
                  >
                    Todos os produtos
                    <QuestionCircleOutlined className='ml-1.5' />
                  </Button>
                </Tooltip>
              </Col>

              <Col xs={12}>
                <Button
                  size='large'
                  style={{ paddingTop: 30, paddingBottom: 30, width: '100%' }}
                  type={productSelectType === ProductSelectType.custom ? 'primary' : 'default'}
                  onClick={setCustomProductSelectType}
                >
                  Produtos específicos
                </Button>
              </Col>

              {productSelectType === ProductSelectType.custom && (
                <Col span={24}>
                  <div className='mt-4!'>
                    <Field
                      id='product_id'
                      name='product_id'
                      endpoint='/webhook/products'
                      single={false}
                      fullWidth
                      component={SelectProduct}
                      selected={values.product_id}
                    />
                  </div>
                </Col>
              )}
            </Row>
          </Col>

          <Col span={24}>
            <Field
              id='content_type'
              name='content_type'
              label='Formato de envio *'
              helperText={
                <>
                  Cabeçalho Content Type. Para saber mais{' '}
                  <a href={MOZ_CONTENT_TYPE_URL} target='_blank' rel='noreferrer'>
                    clique aqui.
                  </a>
                </>
              }
              fullWidth
              options={[
                { label: 'JSON', value: WebhookContentTypeAlias.json },
                { label: 'URL Encoded', value: WebhookContentTypeAlias.urlencoded }
              ]}
              component={SelectFormField}
              onChange={unvalidateOnChange}
            />
          </Col>

          <Col span={24}>
            <Field
              id='url'
              name='url'
              label='URL para envio de dados *'
              fullWidth
              component={UrlValidationFormField}
              resetValidation={resetValidation}
              helperText='Insira uma URL válida, obrigatório http:// ou https://'
              onValidateUrl={setIsUrlValidated}
            />
          </Col>
        </Row>
      </div>

      <div className='flex justify-end gap-2 p-4'>
        <Button disabled={isSubmitting} onClick={onClose}>
          Cancelar
        </Button>

        <Tooltip
          placement='top'
          title={!isUrlValidated && isValid ? 'Sua URL precisa ser validada e retornar status 200 para salvar' : ''}
        >
          <div>
            <Button
              disabled={isSubmitting || !isValid || !isUrlValidated}
              htmlType='submit'
              type='primary'
              icon={isSubmitting ? <Spin size='small' /> : undefined}
            >
              Salvar
            </Button>
          </div>
        </Tooltip>
      </div>
    </>
  );
});

export default Fields;
